import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getVillages, getBeneficiariesByTree, deleteBeneficiary } from '../services/authService';
import { showSuccess, showError, confirmAction } from '../utils/alert';
import AddBeneficiaryModal from '../components/AddBeneficiaryModal';
import FirstPage from '@mui/icons-material/FirstPage';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import LastPage from '@mui/icons-material/LastPage';

// Helper function to organize family members into generations
const organizeByGenerations = (members, relationships, headId) => {
  console.log('Organizing generations with headId:', headId, 'Members:', members, 'Relationships:', relationships);

  // Find the household head
  const head = members.find(m => m.is_household_head) || members.find(m => m.beneficiary_id === headId) || members[0];
  if (!head) {
    console.warn('No head found, returning empty generations');
    return [];
  }

  // Create a map of relationships for easier lookup
  const relationshipMap = {};
  relationships.forEach(rel => {
    if (!relationshipMap[rel.source_id]) relationshipMap[rel.source_id] = [];
    relationshipMap[rel.source_id].push({
      target_id: rel.target_id,
      relationship: rel.relationship,
      inverse_relationship: rel.inverse_relationship
    });
  });

  // First Generation: Parents or in-laws of the head
  const firstGen = members.filter(m => {
    const relationshipsToHead = relationships.filter(
      r => r.target_id === head.beneficiary_id && ['Father', 'Mother', 'Father-in-law', 'Mother-in-law'].includes(r.inverse_relationship)
    );
    return relationshipsToHead.some(r => r.source_id === m.beneficiary_id);
  });

  // Second Generation: Head, spouse, and siblings
  const secondGen = [head];
  const spouseRelationship = relationships.find(
    r => r.source_id === head.beneficiary_id && ['Husband', 'Wife'].includes(r.relationship)
  );
  if (spouseRelationship) {
    const spouse = members.find(m => m.beneficiary_id === spouseRelationship.target_id);
    if (spouse) secondGen.push(spouse);
  }

  // Add siblings (e.g., Brother, Sister) to the second generation
  const siblingRelationships = relationships.filter(
    r => r.target_id === head.beneficiary_id && ['Brother', 'Sister'].includes(r.inverse_relationship)
  );
  siblingRelationships.forEach(rel => {
    const sibling = members.find(m => m.beneficiary_id === rel.source_id);
    if (sibling && !secondGen.some(m => m.beneficiary_id === sibling.beneficiary_id)) {
      secondGen.push(sibling);
    }
  });

  // Third Generation: Children and in-laws of the head
  const thirdGen = members.filter(m => {
    const relationshipsToHead = relationships.filter(
      r => r.source_id === head.beneficiary_id && ['Son', 'Daughter', 'Son-in-law', 'Daughter-in-law'].includes(r.relationship)
    );
    return relationshipsToHead.some(r => r.target_id === m.beneficiary_id);
  });

  // Fourth Generation: Grandchildren
  const fourthGen = [];
  thirdGen.forEach(child => {
    const childRelationships = relationships.filter(
      r => r.source_id === child.beneficiary_id && ['Son', 'Daughter'].includes(r.relationship)
    );
    childRelationships.forEach(rel => {
      const grandchild = members.find(m => m.beneficiary_id === rel.target_id);
      if (grandchild) fourthGen.push(grandchild);
    });
  });

  // Combine generations, filtering out empty ones
  const generations = [];
  if (firstGen.length > 0) generations.push(firstGen);
  if (secondGen.length > 0) generations.push(secondGen);
  if (thirdGen.length > 0) generations.push(thirdGen);
  if (fourthGen.length > 0) generations.push(fourthGen);

  // Fallback: Include any unlinked members in a default generation
  const unlinkedMembers = members.filter(m => !generations.flat().some(gm => gm.beneficiary_id === m.beneficiary_id));
  if (unlinkedMembers.length > 0) {
    generations.push(unlinkedMembers);
  }

  console.log('Generated generations:', generations);
  return generations;
};

// Helper function to get relationship label
const getRelationship = (relationships, sourceId, targetId) => {
  const relationship = relationships.find(
    r => r.source_id === sourceId && r.target_id === targetId
  );
  return relationship ? relationship.relationship : '';
};

const FamilyTree = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [villages, setVillages] = useState([]);
  const { id } = useParams();
  const householdId = id;
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });

  // Fetch villages for the form
  useEffect(() => {
    async function fetchVillages() {
      try {
        const asha_id = localStorage.getItem('asha_id');
        const response = await getVillages(asha_id);
        if (response.status === true) {
          setVillages(response.data);
        } else {
          showError(response.message || 'Failed to fetch villages');
        }
      } catch (err) {
        showError('Network error while fetching villages');
      }
    } 
    fetchVillages();
  }, []);

  // Fetch household data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!householdId) {
          throw new Error('Household ID is missing');
        }

        console.log('Fetching data for householdId:', householdId, 'Page:', pagination.page);
        const response = await getBeneficiariesByTree(householdId, pagination.page);
        const result = response;
        console.log('API response:', result);

      
    console.log('members', result);
        if (result.status && result.data) {
          const members = result.data.data || result.data;
      
          if (!Array.isArray(members)) {
            console.error('Members is not an array:', members);
            throw new Error('Expected members to be an array');
          }

          // Update pagination meta
          setPagination(prev => ({
            ...prev,
            totalPages: result.data.meta.last_page || 1,
          }));

          if (members.length === 0) {
            setHouseholdData({
              household: {
                household_id: householdId,
                head_name: 'Unknown',
                village_name: 'Unknown',
                address: 'N/A',
                registered_date: new Date().toISOString().split('T')[0],
                total_members: 0,
                pregnant_count: 0,
                children_count: 0,
                elderly_count: 0,
              },
              members: [],
              relationships: [],
            });
            setGenerations([]);
            return;
          }

          // Process members and relationships
          const allMembers = [
            ...members.map(m => ({
              ...m,
              is_household_head: m.family_relationships.some(fr => ['Father', 'Husband', 'Mother'].includes(fr.relationship)) || false,
            })),
            ...members.flatMap(m =>
              m.family_relationships.map(fr => ({
                beneficiary_id: fr.related_beneficiary_id,
                name: fr.related_name,
                gender: fr.related_gender,
                date_of_birth: null,
                age: fr.related_age,
                category: fr.related_category,
                village_name: m.village_name,
                family_relationships: [],
                is_household_head: false,
              }))
            ),
          ];

          // Remove duplicates
          const uniqueMembers = Array.from(
            new Map(allMembers.map(m => [m.beneficiary_id, m])).values()
          );

          // Create relationships array
          const relationships = members.flatMap(m =>
            m.family_relationships.map(fr => ({
              source_id: m.beneficiary_id,
              target_id: fr.related_beneficiary_id,
              relationship: fr.relationship,
              inverse_relationship: fr.inverse_relationship || fr.relationship,
            }))
          );

          // Construct household data
          const data = {
            household: {
              household_id: householdId,
              head_name: uniqueMembers.find(m => m.is_household_head)?.name || uniqueMembers[0]?.name || 'Unknown',
              village_name: uniqueMembers[0]?.village_name || 'Unknown',
              address: 'N/A',
              registered_date: new Date().toISOString().split('T')[0],
              total_members: uniqueMembers.length,
              pregnant_count: uniqueMembers.filter(m => m.category === 'Pregnant Woman').length,
              children_count: uniqueMembers.filter(m => m.category === 'Child').length,
              elderly_count: uniqueMembers.filter(m => m.category === 'Elderly').length,
            },
            members: uniqueMembers,
            relationships,
          };

          console.log('Processed household data:', data);
          setHouseholdData(data);
          const headId = data.members.find(m => m.is_household_head)?.beneficiary_id || data.members[0]?.beneficiary_id;
          const generationsArray = organizeByGenerations(data.members, data.relationships, headId);
          setGenerations(generationsArray);
        } else {
          throw new Error(result.message || 'Invalid API response');
        }
      } catch (error) {
        console.error('Error fetching household data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [householdId, pagination.page]);

  // Handle save callback from modal
  const handleSave = async () => {
    try {
      const response = await getBeneficiariesByTree(householdId, pagination.page);
      const result = response;   
      if (result.status && result.data) {
        const members = result.data.data || result.data;
        if (!Array.isArray(members)) {
          throw new Error('Expected members to be an array');
        }

        // Update pagination meta
        setPagination(prev => ({
          ...prev,
          totalPages: result.data.meta.last_page || 1,
        }));

        const allMembers = [
          ...members.map(m => ({
            ...m,
            is_household_head: m.family_relationships.some(fr => ['Father', 'Husband', 'Mother'].includes(fr.relationship)) || false,
          })),
          ...members.flatMap(m =>
            m.family_relationships.map(fr => ({
              beneficiary_id: fr.related_beneficiary_id,
              name: fr.related_name,
              gender: fr.related_gender,
              date_of_birth: null,
              age: fr.related_age,
              category: fr.related_category,
              village_name: m.village_name,
              family_relationships: [],
              is_household_head: false,
            }))
          ),
        ];

        const uniqueMembers = Array.from(
          new Map(allMembers.map(m => [m.beneficiary_id, m])).values()
        );

        const relationships = members.flatMap(m =>
          m.family_relationships.map(fr => ({
            source_id: m.beneficiary_id,
            target_id: fr.related_beneficiary_id,
            relationship: fr.relationship,
            inverse_relationship: fr.inverse_relationship || fr.relationship,
          }))
        );

        const data = {
          household: {
            household_id: householdId,
            head_name: uniqueMembers.find(m => m.is_household_head)?.name || uniqueMembers[0]?.name || 'Unknown',
            village_name: uniqueMembers[0]?.village_name || 'Unknown',
            address: 'N/A',
            registered_date: new Date().toISOString().split('T')[0],
            total_members: uniqueMembers.length,
            pregnant_count: uniqueMembers.filter(m => m.category === 'Pregnant Woman').length,
            children_count: uniqueMembers.filter(m => m.category === 'Child').length,
            elderly_count: uniqueMembers.filter(m => m.category === 'Elderly').length,
          },
          members: uniqueMembers,
          relationships,
        };

        console.log('Refreshed household data:', data);
        setHouseholdData(data);
        const headId = data.members.find(m => m.is_household_head)?.beneficiary_id || data.members[0]?.beneficiary_id;
        const generationsArray = organizeByGenerations(data.members, data.relationships, headId);
        setGenerations(generationsArray);
      }
    } catch (err) {
      showError('Failed to refresh family tree data');
    }
  };

  // Handle modal open/close
  const handleAddMember = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const onEdit = (beneficiaryId) => {
    setSelectedBeneficiaryId(beneficiaryId);
    setShowModal(true);
  };

  // Handle delete beneficiary
  const onDelete = async (beneficiaryId) => {
    console.log('beneficiaryId', beneficiaryId);
    const isConfirmed = await confirmAction('Are you sure you want to delete this beneficiary?');
    if (isConfirmed) {
      try {
        const response = await deleteBeneficiary(beneficiaryId);
        if (response.status) {
          showSuccess('Beneficiary deleted successfully');
          // Update the generations state by filtering out the deleted beneficiary
          setGenerations((prevGenerations) =>
            prevGenerations
              .map((generation) =>
                generation.filter((b) => b.beneficiary_id !== beneficiaryId)
              )
              .filter((generation) => generation.length > 0) // Remove empty generations
          );
          // Update householdData to reflect the deletion
          setHouseholdData((prevData) => {
            const updatedMembers = prevData.members.filter(
              (m) => m.beneficiary_id !== beneficiaryId
            );
            const updatedRelationships = prevData.relationships.filter(
              (r) =>
                r.source_id !== beneficiaryId && r.target_id !== beneficiaryId
            );
            return {
              ...prevData,
              members: updatedMembers,
              relationships: updatedRelationships,
              household: {
                ...prevData.household,
                head_name:
                  updatedMembers.find((m) => m.is_household_head)?.name ||
                  updatedMembers[0]?.name ||
                  'Unknown',
                total_members: updatedMembers.length,
                pregnant_count: updatedMembers.filter(
                  (m) => m.category === 'Pregnant Woman'
                ).length,
                children_count: updatedMembers.filter(
                  (m) => m.category === 'Child'
                ).length,
                elderly_count: updatedMembers.filter(
                  (m) => m.category === 'Elderly'
                ).length,
              },
            };
          });
        } else {
          showError(response.message || 'Failed to delete beneficiary');
        }
      } catch (err) {
        showError('Network error while deleting beneficiary');
      }
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <span>Loading...</span>
      </div>
    );
  }

  if (error || !householdData) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        Failed to load household data: {error || 'Unknown error'}
      </div>
    );
  }

  const { household, members, relationships } = householdData;

  return (
    <>
      <style>
        {`
          .family-tree-container {
            padding: 20px;
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .edit-button {
            background: #fff;
            color: #1976d2;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }
          .edit-button:hover {
            background: #f0f0f0;
          }
          .info-section {
            margin: 20px 0;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 10px;
          }
          .info-details {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          .info-item {
            font-size: 14px;
            flex: none;
          }
          .stats-container {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;
            margin-top: 20px;
          }
          .stat-card {
            background: #f0f8ff;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            min-width: 120px;
          }
          .stat-number {
            font-size: 24px;
            font-weight: bold;
            color: #1976d2;
          }
          .stat-label {
            font-size: 14px;
            color: #555;
          }
          .divider {
            border: none;
            border-top: 1px solid #e0e0e0;
            margin: 20px 0;
          }
          .generation-container {
            display: flex;
            justify-content: center;
            margin-bottom: 40px;
            position: relative;
            flex-wrap: wrap;
          }
          .member-card {
            width: 180px;
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            text-align: center;
            margin: 15px;
            position: relative;
            transition: transform 0.3s ease;
          }
          .member-card:hover {
            transform: translateY(-5px);
          }
          .member-card-head {
            border-top: 5px solid #2196f3;
          }
          .member-card-female {
            border-top: 5px solid #e91e63;
          }
          .member-card-other {
            border-top: 5px solid #9e9e9e;
          }
          .household-head {
            border: 2px solid #ff9800;
          }
          .head-badge {
            position: absolute;
            top: -10px;
            right: -10px;
            background: #ff9800;
            color: white;
            border-radius: 50%;
            width: 25px;
            height: 25px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
          }
          .relationship-label {
            position: absolute;
            top: -25px;
            left: 50%;
            transform: translateX(-50%);
            background: #f5f5f5;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            color: #555;
            white-space: nowrap;
          }
          .member-name {
            font-size: 16px;
            font-weight: 500;
            margin: 10px 0 5px;
          }
          .member-details {
            font-size: 14px;
            color: #555;
            margin-bottom: 10px;
          }
          .category-chip {
            display: inline-flex;
            align-items: center;
            padding: 5px 10px;
            border-radius: 16px;
            font-size: 12px;
            margin-bottom: 10px;
          }
          .action-buttons-tree {
            display: flex;
            justify-content: center;
            gap: 10px;
            padding: 10px;
          }
          .action-button-tree {
            background: #1976d2;
            color: white;
            border: none;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
          }
          .action-button-tree:hover {
            background: #1565c0;
          }
          .add-button {
            background: #1976d2;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            margin-bottom: 20px;
          }
          .add-button:hover {
            background: #1565c0;
          }
          .members-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          .members-table th, .members-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid #e0e0e0;
          }
          .members-table th {
            background: #f5f5f5;
            font-weight: 600;
          }
          .chip {
            display: inline-flex;
            padding: 5px 10px;
            border-radius: 16px;
            font-size: 12px;
            margin: 2px;
          }
          .category-pregnant {
            background: #fff8e1;
            color: #ff8f00;
          }
          .category-child {
            background: #e3f2fd;
            color: #1976d2;
          }
          .category-elderly {
            background: #e8eaf6;
            color: #3949ab;
          }
          .category-other {
            background: #e8f5e9;
            color: #2e7d32;
          }
          .relationship-chip {
            background: #e3f2fd;
            color: #1976d2;
          }
          .head-chip {
            background: #ff9800;
            color: white;
          }
          .view-button, .edit-table-button {
            background: #1976d2;
            color: white;
            border: none;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            margin-right: 5px;
          }
          .view-button:hover, .edit-table-button:hover {
            background: #1565c0;
          }
          .category-icon {
            margin-right: 5px;
            font-size: 14px;
          }
          .connector {
            position: absolute;
            top: -30px;
            left: 50%;
            height: 30px;
            border-left: 1px solid #bdbdbd;
          }
          .pagination {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 20px;
            gap: 5px;
          }
          .pagination-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background: #1976d2;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: background 0.3s ease;
          }
          .pagination-button:hover {
            background: #1565c0;
          }
          .pagination-button:disabled {
            background: #bdbdbd;
            cursor: not-allowed;
          }
          .pagination-button.active {
            background: #ff9800;
            font-weight: bold;
          }
        `}
      </style>

      <h2>Family Tree</h2>

      <div className="family-tree-container">
        {/* Household Information */}
        <div className="info-section">
          <div className="section-title">{household.head_name}'s Family</div>
          <div className="info-details">
            <span className="info-item"><strong>Village:</strong> {household.village_name}</span>
            <span className="info-item"><strong>Address:</strong> {household.address}</span>
            <span className="info-item"><strong>Registered:</strong> {new Date(household.registered_date).toLocaleDateString()}</span>
          </div>
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-number">{household.total_members}</div>
              <div className="stat-label">Members</div>
            </div>
            <div className="stat-card" style={{ background: '#fff8e1' }}>
              <div className="stat-number" style={{ color: '#ff8f00' }}>{household.pregnant_count}</div>
              <div className="stat-label">Pregnant</div>
            </div>
            <div className="stat-card" style={{ background: '#e3f2fd' }}>
              <div className="stat-number" style={{ color: '#1976d2' }}>{household.children_count}</div>
              <div className="stat-label">Children</div>
            </div>
            <div className="stat-card" style={{ background: '#e8eaf6' }}>
              <div className="stat-number" style={{ color: '#3949ab' }}>{household.elderly_count}</div>
              <div className="stat-label">Elderly</div>
            </div>
          </div>
        </div>

        <hr className="divider" />

        {/* Family Tree Visualization */}
        <div className="section-title">Family Tree</div>
        <button className="add-button" onClick={handleAddMember}>
          <i className="fas fa-plus"></i> Add Family Member
        </button>

        <div style={{ overflowX: 'auto', padding: '20px 0' }}>
          <div style={{ minWidth: '800px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {generations.length === 0 ? (
              <div>No family members to display. Add members to build the family tree.</div>
            ) : (
              generations.map((generation, genIndex) => (
                <div key={`gen-${genIndex}`} className="generation-container">
                  {generation.map((member) => {
                    let relationshipLabel = '';
                    const headId = members.find(m => m.is_household_head)?.beneficiary_id || members[0]?.beneficiary_id;
                    if (genIndex === 0) {
                      if (headId) {
                        relationshipLabel = getRelationship(relationships, member.beneficiary_id, headId);
                      }
                    } else if (genIndex === 1) {
                      if (member.beneficiary_id !== headId) {
                        relationshipLabel = getRelationship(relationships, headId, member.beneficiary_id);
                      }
                    } else {
                      const previousGen = generations[genIndex - 1];
                      if (previousGen) {
                        for (const potentialParent of previousGen) {
                          const relationship = getRelationship(relationships, potentialParent.beneficiary_id, member.beneficiary_id);
                          if (['Son', 'Daughter', 'Son-in-law', 'Daughter-in-law'].includes(relationship)) {
                            relationshipLabel = relationship;
                            break;
                          }
                        }
                      }
                    }

                    const borderClass = member.gender === 'Male' ? 'member-card-head' :
                                      member.gender === 'Female' ? 'member-card-female' :
                                      'member-card-other';
                    const isHead = member.is_household_head ? 'household-head' : '';

                    const getCategoryIcon = () => {
                      switch (member.category) {
                        case 'Pregnant Woman': return 'fas fa-female';
                        case 'Child': return 'fas fa-child';
                        case 'Elderly': return 'fas fa-user-alt';
                        default: return member.gender === 'Male' ? 'fas fa-male' : 'fas fa-female';
                      }
                    };

                    return (
                      <div key={`member-${member.beneficiary_id}`} style={{ position: 'relative' }}>
                        {genIndex > 0 && <div className="connector"></div>}
                        <div className={`member-card ${borderClass} ${isHead}`}>
                          {member.is_household_head && <div className="head-badge">H</div>}
                          {relationshipLabel && <div className="relationship-label">{relationshipLabel}</div>}
                          <div className="member-name">{member.name}</div>
                          <div className="member-details">{member.age} years, {member.gender}</div>
                          <div className={`category-chip category-${member.category.toLowerCase().replace(' ', '-')}`}>
                            <i className={getCategoryIcon() + ' category-icon'}></i>
                            {member.category}
                          </div>
                          <div className="action-buttons-tree">
                            <button className="action-button-tree" onClick={() => onEdit(member.beneficiary_id)}>Edit</button>
                            <button className="action-button-tree" onClick={() => onDelete(member.beneficiary_id)}>Delete</button>
                          </div>
                        </div>  
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>

       

        <hr className="divider" />

        {/* Family Members List */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div className="section-title">Household Members</div>
        </div>

        <table className="members-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Age/Gender</th>
              <th>Category</th>
              <th>Relationships</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => (
              <tr key={member.beneficiary_id}>
                <td>
                  {member.name}
                  {member.is_household_head && (
                    <span className="chip head-chip" style={{ marginLeft: '5px' }}>Head</span>
                  )}
                </td>
                <td>{member.age} years, {member.gender}</td>
                <td>
                  <span className={`chip category-${member.category.toLowerCase().replace(' ', '-')}`}>
                    {member.category}
                  </span>
                </td>
                <td>
                  {relationships
                    .filter(rel => rel.source_id === member.beneficiary_id)
                    .map(rel => {
                      const relatedMember = members.find(m => m.beneficiary_id === rel.target_id);
                      return relatedMember ? (
                        <span
                          key={`rel-${rel.target_id}`}
                          className="chip relationship-chip"
                        >
                          {rel.relationship} of {relatedMember.name}
                        </span>
                      ) : null;
                    })}
                </td>
                <td>
                  <button className="view-button" onClick={() => onEdit(member.beneficiary_id)}>View</button>
                  <button className="edit-table-button" onClick={() => onEdit(member.beneficiary_id)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody> 
        </table>

        
      </div>

      {/* Add Beneficiary Modal */}
      <AddBeneficiaryModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        householdId={householdId}
        villages={villages}
        beneficiaryId={selectedBeneficiaryId}
      />
    </>
  );
};

export default FamilyTree;  